// =====================================================================================
// FILE: src/hooks/useVersionCheck.js
// Polls /version.json to detect new deployments and prompt users to refresh.
// Drop into both club and commercial repos.
// =====================================================================================
import { useState, useEffect, useRef } from "react";

const CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const knownVersionRef = useRef(null);

  useEffect(() => {
    let active = true;

    const checkVersion = async () => {
      try {
        // Cache-bust to always get fresh version.json
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const serverVersion = data.buildTime || data.version || "";

        if (!serverVersion) return;

        if (knownVersionRef.current === null) {
          // First check — store the current version
          knownVersionRef.current = serverVersion;
        } else if (serverVersion !== knownVersionRef.current) {
          // Version changed — new deployment detected
          console.log(`[VersionCheck] New version detected: ${knownVersionRef.current} → ${serverVersion}`);
          if (active) setUpdateAvailable(true);
        }
      } catch {
        // Silently ignore — version.json might not exist yet
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const refresh = () => {
    window.location.reload();
  };

  return { updateAvailable, refresh };
}
